import { useEffect, useState } from 'react';

import { ArchiveDropdown } from 'pages/Workshop/components/ArchiveDropdown';
import { TabLabel } from 'pages/Workshop/components/TabLabel';
import { WorkshopTabsTable } from 'pages/Workshop/components/WorkshopTabsTable';

import styles from '../ShowingsTable/styles.module.scss';

import { WorkshopPageTabs } from 'pages/Workshop/components/WorkshopPageTabs';
import {
  emptyText,
  SHOWING_TABS,
  prepareShowingData,
  getArchivedShowingOptions,
  archivedShowingsDataSource,
} from './helper';
import {
  getActiveShowingTabSelector,
  getShowingAggregateDataSelector,
} from 'store/selectors/showingAggregate';
import { useDispatch, useSelector } from 'react-redux';
import { tableColumns } from '../ShowingsTable/tableColumns';
import { useManageShowingsList } from './useManageShowingsList';
import { ConfirmationWithReasonModal } from 'components-antd';
import { AppointmentDetailsModal } from '../AppointmentDetailsModal';
import { ApprovalAction } from 'types';
import { useLocation } from 'react-router-dom';
import { locationState } from '../ShowingsTable';
import { setShowingsTabEffect } from 'store/effects/showingAggregate';

export const ShowingTabs = ({ fetchShowingAggregate }) => {
  const [archivedValue, setArchivedValue] = useState('');
  const { activeShowingTab } = useSelector(getActiveShowingTabSelector);
  const [activeKey, setActiveKey] = useState(activeShowingTab || SHOWING_TABS.Pending);

  const showingAggregate = useSelector(getShowingAggregateDataSelector);

  const location = useLocation();
  const historyState = location.state as locationState;
  const dispatch = useDispatch();

  const {
    data,
    setData,
    showModal,
    isFeedback,
    processData,
    actionConfig,
    processAction,
    onDetailsClose,
    onReasonConfirm,
    pendingApprovers,
    appointmentModal,
    isApprovalRequired,
    redirectToOverview,
    onConfirmationCancel,
    getPendingApproverNames,
  } = useManageShowingsList({ fetchShowingAggregate, activeTab: activeKey });

  const { pending, newFeedback, today, tomorrow, thisWeek, upcoming, archivedShowings } =
    prepareShowingData({ showingAggregate });

  const tableProps = {
    columns: tableColumns(false, processAction, setData, getPendingApproverNames, false),
    onRow: (record) => {
      return {
        onClick: (event) => {
          redirectToOverview(record);
        },
      };
    },
    tableClassName: styles.tableView,
  };

  const archivedDataSource = archivedShowingsDataSource(archivedValue, archivedShowings);
  const tabItems = [
    {
      key: SHOWING_TABS.Pending,
      label: <TabLabel title={SHOWING_TABS.Pending} count={pending.length} />,
      children: <WorkshopTabsTable {...tableProps} dataSource={pending} emptyText={emptyText} />,
    },
    {
      key: SHOWING_TABS.NewFeedback,
      label: <TabLabel title={SHOWING_TABS.NewFeedback} count={newFeedback.length} />,
      children: (
        <WorkshopTabsTable {...tableProps} dataSource={newFeedback} emptyText={emptyText} />
      ),
    },
    {
      key: SHOWING_TABS.Today,
      label: <TabLabel title={SHOWING_TABS.Today} count={today.length} />,
      children: <WorkshopTabsTable {...tableProps} dataSource={today} emptyText={emptyText} />,
    },
    {
      key: SHOWING_TABS.Tomorrow,
      label: <TabLabel title={SHOWING_TABS.Tomorrow} count={tomorrow.length} />,
      children: <WorkshopTabsTable {...tableProps} dataSource={tomorrow} emptyText={emptyText} />,
    },
    {
      key: SHOWING_TABS.ThisWeek,
      label: <TabLabel title={SHOWING_TABS.ThisWeek} count={thisWeek.length} />,
      children: <WorkshopTabsTable {...tableProps} dataSource={thisWeek} emptyText={emptyText} />,
    },
    {
      key: SHOWING_TABS.Upcoming,
      label: <TabLabel title={SHOWING_TABS.Upcoming} count={upcoming.length} />,
      children: <WorkshopTabsTable {...tableProps} dataSource={upcoming} emptyText={emptyText} />,
    },
    {
      key: SHOWING_TABS.Archive,
      label: (
        <ArchiveDropdown
          archivedValue={archivedValue}
          setArchivedValue={(value) => {
            setArchivedValue(value);
            setActiveKey(SHOWING_TABS.Archive);
          }}
          archivedData={archivedDataSource}
          dropdownOptions={getArchivedShowingOptions(archivedShowings)}
        />
      ),
      children: archivedValue ? (
        <WorkshopTabsTable {...tableProps} dataSource={archivedDataSource} emptyText={emptyText} />
      ) : null,
    },
  ];

  const resetShowingTab = () => {
    activeShowingTab && dispatch(setShowingsTabEffect(null));
  };

  return (
    <div>
      <WorkshopPageTabs
        activeKey={activeKey}
        items={tabItems}
        onChange={(value) => {
          resetShowingTab();
          if (archivedValue && value !== SHOWING_TABS.Archive) {
            setArchivedValue('');
          }

          value !== SHOWING_TABS.Archive && setActiveKey(value);
        }}
      />

      <ConfirmationWithReasonModal
        open={showModal}
        confirmText={<span>Are you sure you want to {actionConfig?.action} this showing?</span>}
        placeholder={`Write a reason for ${
          actionConfig?.action === ApprovalAction.Decline ? 'declining' : 'cancelling'
        }...`}
        okText={actionConfig?.action === ApprovalAction.Cancel ? 'Confirm' : actionConfig?.action}
        showReason={!(actionConfig?.action === ApprovalAction.Approve)}
        onOk={onReasonConfirm}
        onCancel={onConfirmationCancel}
      />

      {data !== null && (
        <AppointmentDetailsModal
          isOpen={appointmentModal}
          onClose={onDetailsClose}
          UUID={(data?.UUID || historyState?.showingAppointmentUUID)?.toString()}
          isApprovalRequired={isApprovalRequired}
          pendingApprovers={pendingApprovers}
          processAction={processAction}
          isFeedback={isFeedback}
          processData={processData}
        />
      )}
    </div>
  );
};
